import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { Clickable, Stack, Text } from '@moda/om';
import ChevronDownIcon from '@moda/icons/chevron-down-12';
import ChevronUpIcon from '@moda/icons/chevron-up-12';
import ShippingAltIcon from '@moda/icons/shipping-alt-20';
import { hrefFor } from '../../../routers';
import { tc } from '../../../lib/trackingContext';
import { usePageType } from '../../../hooks/usePageType';
import { Link } from '../../Link';
import { TrunkshowCountdownTimer } from '../../TrunkshowCountdownTimer';
import { PdpInfoContainerFragment } from '../../../generated/types';

import './PdpTrunkshowInfo.scss';

type PdpTrunkshow = NonNullable<
  NonNullable<
    NonNullable<NonNullable<PdpInfoContainerFragment['trunkshows']>['edges']>[number]
  >['node']
>;

interface Props {
  mode: 'pdp' | 'trunkshowPage';
  trunkshow?: PdpTrunkshow;
  shippingDateFormatted?: string;
  pdpVersion?: boolean;
  isDropshipItem?: boolean;
  header?: React.ReactNode;
}

export const PdpTrunkshowInfo: React.FC<Props> = ({
  mode = 'pdp',
  trunkshow,
  shippingDateFormatted,
  pdpVersion,
  isDropshipItem,
  header
}) => {
  const { vertical = 'women' } = useParams<{ vertical?: undefined }>();
  const [expanded, setExpanded] = useState<boolean>(false);
  const pagetype = usePageType();

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      setExpanded(expanded => !expanded);
      tc.track('What is Trunkshow Clicked', {
        addToTrackingEvent: {
          pagetype,
          coordinateX: event?.pageX || 0,
          coordinateY: event?.pageY || 0
        }
      });
    },
    [pagetype]
  );

  const ctaLabel = mode === 'trunkshowPage' ? 'What is a Trunkshow?' : 'Learn More';

  const contentArray = [
    <Stack key={1} space={3} direction="horizontal" className="PdpTrunkshowInfo__section-content">
      <Text className="PdpTrunkshowInfo__title-number" color="cement">
        1
      </Text>
      <div className="PdpTrunkshowInfo__text">
        <Text treatment="h6" className="PdpTrunkshowInfo__title-text">
          LIMITED TIME
        </Text>
        <Text treatment="h6" className="PdpTrunkshowInfo__trunkshow-description" color="cement">
          Trunkshows are typically live for 2-4 weeks.
        </Text>
      </div>
    </Stack>,
    <Stack key={2} space={3} direction="horizontal" className="PdpTrunkshowInfo__section-content">
      <Text className="PdpTrunkshowInfo__title-number" color="cement">
        2
      </Text>
      <div className="PdpTrunkshowInfo__text">
        <Text treatment="h6" className="PdpTrunkshowInfo__title-text">
          DIRECTLY FROM THE RUNWAY
        </Text>
        <Text treatment="h6" className="PdpTrunkshowInfo__trunkshow-description" color="cement">
          You get exclusive access to styles (and sizes) that often don't make it into stores.
        </Text>
      </div>
    </Stack>,
    <Stack key={3} space={3} direction="horizontal" className="PdpTrunkshowInfo__section-content">
      <Text className="PdpTrunkshowInfo__title-number" color="cement">
        3
      </Text>
      <div className="PdpTrunkshowInfo__text">
        <Text treatment="h6" className="PdpTrunkshowInfo__title-text">
          HALF NOW, HALF LATER
        </Text>
        <Text treatment="h6" className="PdpTrunkshowInfo__trunkshow-description" color="cement">
          You pay a 50% deposit with purchase, and the other 50% is charged when the item is ready
          to ship.
        </Text>
      </div>
    </Stack>
  ];

  const allowExtraTitle = header != undefined;

  const trunkshowPageCopy = (
    <Stack space={3} className="PdpTrunkshowInfo__trunkshow-page-copy">
      <Text treatment="h6">
        <span className="PdpTrunkshowInfo__bold">See It. Love it. Own it.</span> A Trunkshow is your
        opportunity to pre-order directly from the runway, ahead of the crowd. Each Trunkshow is
        live for a limited amount of time, and many styles featured will never make it into
        stores—meaning you'll be one of a select few to secure a particular look. You pay 50% at the
        time of purchase, and the other half is charged when the item ships. Trunkshow delivery
        windows follow each designer's production schedule—click through to individual items for an
        ETA (if you add a product to cart, you can also see a timeline there).
      </Text>
      <Text treatment="h6">
        <span className="PdpTrunkshowInfo__bold">Need more guidance?</span> Reach out to{' '}
        <a className="PdpTrunkshowInfo__email" href="mailto:private@modaoperandi.com">
          private@modaoperandi.com
        </a>{' '}
        with any with any questions—our stylists are happy to help.
      </Text>
    </Stack>
  );

  return (
    <Stack space={5} className="PdpTrunkshowInfo" data-testid="infoModuleSection">
      <div
        className={classNames('PdpTrunkshowInfo__main', {
          'PdpTrunkshowInfo__main--wide': !pdpVersion
        })}
      >
        <div
          className={classNames(
            allowExtraTitle ? 'PdpTrunkshowInfo__title-grid' : 'PdpTrunkshowInfo__title-new',
            {
              'PdpTrunkshowInfo__title-new--wide': !pdpVersion,
              'PdpTrunkshowInfo__title-new--center-sm': mode === 'trunkshowPage'
            }
          )}
        >
          {allowExtraTitle ? (
            header
          ) : mode === 'pdp' && trunkshow ? (
            <Link
              to={hrefFor.TrunkshowPage({
                vertical,
                trunkshowId: trunkshow.id,
                trunkshowSlug: trunkshow.slug as string
              })}
              className="PdpTrunkshowInfo__title-link"
            >
              <Text
                treatment="bold1"
                className={classNames('PdpTrunkshowInfo__trunkshow-name', {
                  'PdpTrunkshowInfo__trunkshow-name--wide': !pdpVersion
                })}
              >
                {trunkshow.name} {trunkshow.seasonName}
              </Text>
            </Link>
          ) : null}

          <Clickable aria-label={ctaLabel} onClick={handleClick}>
            <Stack
              space={1}
              direction="horizontal"
              alignItems="center"
              className={classNames({ 'PdpTrunkshowInfo__title-right': !allowExtraTitle })}
              justifyContent={allowExtraTitle ? 'flex-end' : undefined}
            >
              <Text>{ctaLabel}</Text>
              <div
                className={classNames('PdpTrunkshowInfo__icons', {
                  'PdpTrunkshowInfo__icons--expanded': expanded
                })}
              >
                <ChevronUpIcon width="16px" height="16px" className="PdpTrunkshowInfo__icon-up" />
                <ChevronDownIcon
                  width="16px"
                  height="16px"
                  className="PdpTrunkshowInfo__icon-down"
                />
              </div>
            </Stack>
          </Clickable>
        </div>

        {expanded &&
          (mode === 'trunkshowPage' ? (
            <div
              className={classNames('PdpTrunkshowInfo__expanded', {
                'PdpTrunkshowInfo__expanded--wide': !pdpVersion
              })}
            >
              <div className="PdpTrunkshowInfo__section">
                <Text treatment="h6" className="PdpTrunkshowInfo__trunkshow-description">
                  {trunkshowPageCopy}
                </Text>
              </div>
            </div>
          ) : (
            <div
              className={classNames('PdpTrunkshowInfo__expanded', {
                'PdpTrunkshowInfo__expanded--wide': !pdpVersion
              })}
            >
              {contentArray.map((section, index) => (
                <div
                  key={index}
                  className={classNames('PdpTrunkshowInfo__section', {
                    'PdpTrunkshowInfo__section--wide': !pdpVersion
                  })}
                >
                  {section}
                </div>
              ))}
            </div>
          ))}
      </div>

      {mode === 'pdp' && (
        <Stack space={2} className="PdpTrunkshowInfo__extra-info">
          {shippingDateFormatted && !isDropshipItem && (
            <div className="PdpTrunkshowInfo__shipping">
              <ShippingAltIcon className="PdpTrunkshowInfo__shipping_icon" />
              Ships by {shippingDateFormatted}
            </div>
          )}

          {trunkshow?.activeEndI && (
            <TrunkshowCountdownTimer
              className="PdpTrunkshowInfo__timer"
              endingText="Trunkshow ends in"
              countdownTime={trunkshow.activeEndI}
              countdownEndMessage="This Trunkshow Has Expired"
              hasZeroes={false}
              hasIcon
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
